import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { SvgAttachment } from '@actual-app/components/icons/v1';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { useQuery } from '@tanstack/react-query';

import { attachmentQueries } from '#attachments/queries';
import { pushModal } from '#modals/modalsSlice';
import { useDispatch } from '#redux';

type TransactionAttachmentsButtonProps = {
  transactionId: string;
  variant?: 'desktop' | 'mobile';
};

export function TransactionAttachmentsButton({
  transactionId,
  variant = 'desktop',
}: TransactionAttachmentsButtonProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { data: attachments = [] } = useQuery(
    attachmentQueries.list(transactionId),
  );
  const count = attachments.length;
  const label = t('Attachments ({{count}})', { count });

  function openAttachments() {
    dispatch(
      pushModal({
        modal: {
          name: 'transaction-attachments',
          options: { transactionId },
        },
      }),
    );
  }

  if (variant === 'mobile') {
    return (
      <Button
        aria-label={label}
        data-testid="transaction-attachments-button"
        variant="bare"
        style={{ width: '100%', minHeight: 44, padding: '8px 5px' }}
        onPress={openAttachments}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <SvgAttachment width={18} height={18} />
            <Text>
              <Trans>Attachments</Trans>
            </Text>
          </View>
          <Text>{count}</Text>
        </View>
      </Button>
    );
  }

  return (
    <Button
      aria-label={label}
      data-testid="transaction-attachments-button"
      variant="bare"
      style={{ minHeight: 26, padding: 5 }}
      onPress={openAttachments}
    >
      <SvgAttachment width={14} height={14} />
      <Text aria-hidden="true" style={{ marginLeft: 3 }}>
        {count}
      </Text>
    </Button>
  );
}
